import { useState } from 'react';
import { ALargeSmall, ChevronDown, KeyRound, LayoutGrid, LogOut, Moon, Search, Sun } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Kbd,
  Separator,
  SidebarTrigger,
} from '@proerp/ui';
import { useUiStore } from '@/store/uiStore';
import { usePublicBranding } from '@/features/settings/settingsApi';
import { useTheme } from '@/lib/theme';
import { TEXT_SIZE_LABELS, useTextSize, type TextSize } from '@/lib/textSize';
import { logout } from '@/lib/auth';
import { initials } from '@/lib/format';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { ModuleVisibilityDialog } from './ModuleVisibilityDialog';
import { NotificationBell } from './NotificationBell';

/**
 * Site-Header (Muster: shadcn dashboard-01): 48 px, Sidebar-Trigger, Suche
 * (⌘K, ADR 0015). Das Benutzermenü sitzt desktop im Sidebar-Footer (NavUser);
 * mobil (ohne Sidebar) zeigt der Header Branding + Benutzermenü selbst.
 */
export function Topbar() {
  const openSearch = useUiStore((state) => state.setSearchOpen);
  const appLogoDataUrl = usePublicBranding().data?.data.appLogoDataUrl ?? null;

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b bg-background transition-[width,height] ease-linear">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        {/* Ab `tablet` (744px, iPad mini im Hochformat) die einzige Möglichkeit,
            die Sidebar ein-/auszuklappen (§ Tablet-Optimierung) — auf Touch auf
            36px angehoben (28px sonst). */}
        <SidebarTrigger className="-ml-1 hidden tablet:flex touch:size-9" aria-label="Navigation ein-/ausklappen" />
        <Separator orientation="vertical" className="mx-2 hidden h-4 tablet:block" />

        {/* Branding nur mobil — Desktop hat es im Sidebar-Header. */}
        <div className="flex items-center gap-2 tablet:hidden">
          {appLogoDataUrl ? (
            <img src={appLogoDataUrl} alt="Logo" className="h-6 max-w-28 object-contain" />
          ) : (
            <>
              <span className="text-md font-semibold text-foreground">ProERP</span>
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">Beta</span>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => openSearch(true)}
          aria-label="Suche öffnen"
          className="mx-auto hidden h-8 w-full max-w-md items-center gap-2 rounded-md border border-input bg-muted/40 px-3 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring tablet:flex"
        >
          <Search className="size-4 shrink-0" strokeWidth={1.5} aria-hidden />
          <span className="flex-1 truncate">Projekte, Kunden, Artikel, Belege suchen …</span>
          <Kbd>⌘K</Kbd>
        </button>

        <div className="ml-auto flex items-center gap-1 tablet:ml-0">
          <Button
            variant="ghost"
            size="icon"
            className="size-8 tablet:hidden"
            onClick={() => openSearch(true)}
            aria-label="Suche öffnen"
          >
            <Search strokeWidth={1.5} />
          </Button>
          <NotificationBell />
          <TextSizeMenu />
          <ThemeToggle />
          <MobileUserMenu />
        </div>
      </div>
    </header>
  );
}

/**
 * Textgrößen-Menü: iOS gibt die System-Textgröße (Dynamic Type) nicht an
 * Webinhalte weiter — dies ist der App-eigene Ersatz (persistiert, skaliert die
 * ganze App, siehe apps/web/src/lib/textSize.ts).
 */
function TextSizeMenu() {
  const textSize = useTextSize((state) => state.textSize);
  const setTextSize = useTextSize((state) => state.setTextSize);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-8" aria-label="Textgröße" title="Textgröße">
          <ALargeSmall strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Textgröße</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={textSize} onValueChange={(value) => setTextSize(value as TextSize)}>
          {(Object.keys(TEXT_SIZE_LABELS) as TextSize[]).map((size) => (
            <DropdownMenuRadioItem key={size} value={size}>
              {TEXT_SIZE_LABELS[size]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Hell/Dunkel-Umschalter (persistiert; respektiert anfangs die Systemeinstellung). */
function ThemeToggle() {
  const theme = useTheme((state) => state.theme);
  const toggle = useTheme((state) => state.toggle);
  const isDark = theme === 'dark';
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-8"
      onClick={toggle}
      aria-label={isDark ? 'Helles Design' : 'Dunkles Design'}
      title={isDark ? 'Helles Design' : 'Dunkles Design'}
    >
      {isDark ? <Sun strokeWidth={1.5} /> : <Moon strokeWidth={1.5} />}
    </Button>
  );
}

/** Benutzermenü für mobile Ansichten — desktop übernimmt NavUser in der Sidebar. */
function MobileUserMenu() {
  const { data } = useCurrentUser();
  const me = data?.data;
  const display = me?.name || me?.email || 'Benutzer';
  const isRealSession = Boolean(me && !me.isDevSession);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isModulesOpen, setIsModulesOpen] = useState(false);

  const avatar = (
    <Avatar className="size-7">
      <AvatarFallback className="bg-accent text-xs font-medium text-foreground">
        {initials(display)}
      </AvatarFallback>
    </Avatar>
  );

  if (!isRealSession) {
    return (
      <div className="flex items-center gap-2 px-1.5 tablet:hidden">
        {avatar}
        <div className="text-right">
          <p className="text-sm leading-tight text-foreground">{display}</p>
          {me?.isDevSession && (
            <p className="text-xs leading-tight text-warning-500">Dev-Session</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tablet:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-2 px-1.5" aria-label="Benutzermenü">
            {avatar}
            <ChevronDown className="!size-3.5 text-muted-foreground" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="font-normal">
            <span className="block truncate text-sm font-medium text-foreground">{display}</span>
            {me?.email && (
              <span className="block truncate text-xs text-muted-foreground">{me.email}</span>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={() => setTimeout(() => setIsPasswordOpen(true), 0)}>
              <KeyRound strokeWidth={1.5} aria-hidden />
              Passwort ändern
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTimeout(() => setIsModulesOpen(true), 0)}>
              <LayoutGrid strokeWidth={1.5} aria-hidden />
              Module verwalten
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => logout()}>
              <LogOut strokeWidth={1.5} aria-hidden />
              Abmelden
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordDialog open={isPasswordOpen} onOpenChange={setIsPasswordOpen} />
      <ModuleVisibilityDialog open={isModulesOpen} onOpenChange={setIsModulesOpen} />
    </div>
  );
}
